import * as React from 'react';

import type {
  IRegistrationCounts,
  IRegistrationQuery,
  RegistrationCard,
  RegistrationFlag
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { useAdminData } from '../../components/admin-page';
import { useVisitSince } from '../../components/my-tasks-summary';
import type { SignalSpec } from '../../components/state-cards';

/**
 * The Registrations cards and their signals (SR-515) — a work-item-backed work page's, so each is a
 * parameter of `GET /admin/registrations`: the card as `card=`, the signals pressed as `flag=`
 * repeated, an attempt kept where it raises any. The keys are tidy constants, never a data value
 * (the work pages' rule 8).
 */
interface IRegistrationCardSpec {
  /** `all` is no `card=`. */
  key: 'all' | RegistrationCard;
  label: string;
  /** A few words on the card. */
  hint: string;
  /** What it holds — the "?"'s words. */
  about: string;
}

export const REGISTRATION_CARDS: readonly IRegistrationCardSpec[] = [
  {
    key: 'all',
    label: 'All',
    hint: 'every attempt',
    about: 'every self-registration attempt, the ones that named nobody included.'
  },
  {
    key: 'attention',
    label: 'Needs attention',
    hint: 'not finished',
    about:
      'attempts neither completed nor refused — still under way, or stopped, walked away from or swept.'
  },
  {
    key: 'refused',
    label: 'Refused',
    hint: 'number or address refused',
    about:
      'attempts not completed because the number is somebody’s already (number_taken) or the address was refused (duplicate_address, address_belongs_to_another_account). The registrant was told neither.'
  },
  {
    key: 'completed',
    label: 'Completed',
    hint: 'an account made',
    about: 'attempts that completed.'
  }
];

/** The URL's card — one this page does not know is All, and is not sent. */
export function cardOf(raw: Nullable<string>): IRegistrationCardSpec['key'] {
  return REGISTRATION_CARDS.find((card) => card.key === raw)?.key ?? 'all';
}

interface IRegistrationSignal extends SignalSpec {
  key: RegistrationFlag;
  /** The filter line's words. */
  words: string;
  /** What no attempt is, in the empty list's sentence. */
  none: string;
  /** What it counts — the "?"'s words. */
  about: string;
  /** The badge's tooltip, where its label is the short word. */
  tip?: string;
}

export const REGISTRATION_SIGNALS: readonly IRegistrationSignal[] = [
  {
    key: 'new',
    label: 'new',
    words: 'new since your last visit',
    none: 'is new since your last visit',
    tone: 'info',
    about: 'started since your last visit to this page on this browser'
  },
  {
    key: 'stuck',
    label: 'stuck',
    tip: 'Stuck at “is this you?”',
    words: 'stuck at “is this you?”',
    none: 'is stuck at “is this you?”',
    tone: 'attention',
    about: 'stopped at the step that asks the registrant to confirm who the number names'
  }
];

/** The URL's flags, once each, as spelled — one this page does not know is survey-api's 400. */
export function flagsOf(params: URLSearchParams): string[] {
  return [...new Set(params.getAll('flag').filter((flag) => flag !== ''))];
}

/** A flag's words and its empty sentence, or its key as spelled. */
export function flagSaid(key: string): { words: string; none: string } {
  return REGISTRATION_SIGNALS.find((signal) => signal.key === key) ?? { words: key, none: `is ${key}` };
}

/**
 * Every card's count and its signals' under the list's search and filters, never its card, flags,
 * sort or window — so a press moves no number (rule 2). Read again when the search or a filter
 * changes, and by `reload` (*Refresh*); never per page. A failed read is no counts: the cards draw
 * without signals (rule 6). A read that lands marks the visit (`visited`).
 */
export function useRegistrationCounts(query: IRegistrationQuery, visited: () => void) {
  const filters: IRegistrationQuery = {
    ...query,
    card: undefined,
    flag: undefined,
    sort: undefined,
    page: undefined,
    pageSize: undefined
  };
  const asked = JSON.stringify(filters);
  const read = useAdminData<IRegistrationCounts>(
    (signal) => participantsApi.registrationCounts(filters, signal),
    [asked]
  );
  React.useEffect(() => {
    if (read.data) visited();
  }, [read.data, visited]);
  return { counts: read.error ? null : read.data, reading: read.reading, reload: read.reload };
}

/** The viewer's last visit to Registrations on this browser — My tasks' rule (`useLastVisit`) under
 *  `veritant.registrations.lastVisit.<viewer>`; a first visit has no window, and so nothing new. */
export function useRegistrationsVisit(): { since: Nullable<string>; visited: () => void } {
  return useVisitSince('veritant.registrations.lastVisit');
}
